/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { GeoAreaDto, GeoAreaDtoGeoAreaTypeEnum } from '@slr/geo-service-sdk';
import { isEmptyOrNull } from '@slr/shared-library';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'configs';
import { useGeoAreasApi } from 'services/geo-area/useGeoAreasApi';

export const getLeafs = (
  geoAreas: readonly GeoAreaDto[],
  parent: string = '',
  search: string
): (GeoAreaDto & { value: string; parents?: string })[] =>
  geoAreas.flatMap((geoArea) => {
    let parents = parent;

    const isInName = geoArea.name
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase());

    if (geoArea.leaf) {
      return { ...geoArea, parents, value: geoArea.id };
    }
    if (
      isInName &&
      !(
        geoArea.geoAreaType === GeoAreaDtoGeoAreaTypeEnum.Nation ||
        geoArea.geoAreaType === GeoAreaDtoGeoAreaTypeEnum.FederalState
      )
    ) {
      const child = geoArea.childGeoAreas ?? [];
      geoArea.childGeoAreas = [...child, { ...geoArea, leaf: true }];
    }

    if (geoArea.childGeoAreas) {
      if (geoArea.geoAreaType !== GeoAreaDtoGeoAreaTypeEnum.Nation)
        parents = `${parents}${isEmptyOrNull(parents) ? '' : ' > '}${geoArea.name}`;
      return getLeafs(geoArea.childGeoAreas, parents, search);
    }

    return [];
  });

const removeSimilar = (array: (GeoAreaDto & { parents?: string })[]) => {
  const result: (GeoAreaDto & { parents?: string })[] = [];
  array.forEach((item) => {
    if (!result.find((i) => i.name === item.name)) result.push(item);
  });
  return result;
};

export const useSearchGeoArea = (search: string = '') => {
  const { searchGeoAreas } = useGeoAreasApi();

  return useQuery({
    queryKey: [queryKeys.searchGeoAreas, { search }],
    queryFn: () =>
      searchGeoAreas({
        search,
        includeAllChildrenOfMatchingAreas: false,
        upToDepth: 0
      }),
    enabled: !isEmptyOrNull(search) && search.length > 2,
    select: (data) => {
      const leafs = getLeafs(data, '', search);

      return removeSimilar(leafs);
    }
  });
};
